import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  SIZE,
  ROLE,
} from 'baseui/modal';
import { Button } from 'components/elements/buttons';
import { SeparatorHorizontal } from 'components/styles';
import { DialogCommonStyles } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
export interface ConfirmationOption {
  title?: string;
  message: string;
  positiveLabel?: string;
  negativeLabel?: string;
  onPositiveAction: (dismiss: VoidFunction) => void;
  onNegativeAction?: (dismiss: VoidFunction) => void;
  noNegative?: boolean;
  onAfterClose?: () => void;
}

interface Props extends ConfirmationOption {
  onClose: () => void;
}

export default function ConfirmationDialog(props: Props) {
  const { t } = useTranslation();
  const {
    title,
    message,
    positiveLabel = t('common:sure'),
    negativeLabel = t('common:no'),
    onPositiveAction,
    onNegativeAction,
    noNegative = false,
  } = props;

  const [isPositiveLoading, setIsPositiveLoading] = React.useState(false);
  const [isNegativeLoading, setIsNegativeLoading] = React.useState(false);

  const onClose = React.useCallback(() => {
    props.onClose();
    props.onAfterClose?.();
  }, [props]);

  const onPositivePress = React.useCallback(async () => {
    setIsPositiveLoading(true);
    await onPositiveAction(props.onClose);
    setIsPositiveLoading(false);
  }, [onPositiveAction, props.onClose]);

  const onNegativePress = React.useCallback(async () => {
    if (onNegativeAction) {
      setIsNegativeLoading(true);
      await onNegativeAction(onClose);
      setIsNegativeLoading(false);
    } else {
      onClose();
    }
  }, [onClose, onNegativeAction]);

  return (
    <Modal
      closeable
      isOpen
      animate
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={false}
      onClose={onClose}
      size={SIZE.default}
      role={ROLE.dialog}
      overrides={DialogCommonStyles}
    >
      {title && <ModalHeader>{title}</ModalHeader>}
      <ModalBody>{message}</ModalBody>
      <ModalFooter $style={{ display: 'flex', justifyContent: 'end' }}>
        {!noNegative && (
          <>
            <Button
              buttonType="tertiary"
              onClick={onNegativePress}
              isLoading={isNegativeLoading}
              disabled={isPositiveLoading || isNegativeLoading}
            >
              {negativeLabel}
            </Button>
            <SeparatorHorizontal $margin={4} />
          </>
        )}
        <Button
          onClick={onPositivePress}
          isLoading={isPositiveLoading}
          disabled={isPositiveLoading || isNegativeLoading}
        >
          {positiveLabel}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
